<template>
  <div :class="['ftr', classes.multiFtrContainer]">
    <div :class="classes.marketCard">
      <CardHeader :eventId="eventId" />

      <EventName
        :class="[classes.eventName, 'event-name']"
        :eventId="eventId"
      />

      <div :class="classes.markets">
        <div
          v-for="(market, index) in homeAwayOptions"
          :key="market?.marketId"
          :class="classes.marketRow"
        >
          <Button
            :stagger="index * 2"
            :optionId="market?.options?.[0]"
            :marketId="market?.marketId"
            :class="classes.marketButton"
            class="away-team"
          />

          <p :class="classes.marketTitle">
            <DynamicText
              :id="marketBranding?.marketNameFont"
              tag="span"
              styleGroup="Bet Market"
              :get="marketNameStyle.get"
              :set="marketNameStyle.set"
              class="market-name"
              >{{ market.displayName }}</DynamicText
            >
          </p>

          <Button
            :stagger="index * 2 + 1"
            :optionId="market?.options?.[1]"
            :marketId="market?.marketId"
            :class="classes.marketButton"
            class="home-team"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, ComputedRef } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";

import Button from "../Button.vue";
import DynamicText from "../../../DynamicText.vue";
import CardHeader from "../../../CardHeader.vue";
import EventName from "../../../EventNamesBadgesVertical.vue";

import { AdTemplateMarket, Brand } from "@fresh8/types/src/index";
import {
  composeEvent,
  composeHomeAwayOptions,
  composeMultiFtrMarkets
} from "@fresh8/ad-data-composition/src/betData";
import composeDate from "@fresh8/ad-data-composition/src/date";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { SportEvent } from "@fresh8/types/src/betData";

export default defineComponent({
  name: "MultiFtrCard",
  components: {
    Button,
    EventName,
    DynamicText,
    CardHeader
  },
  props: {
    marketBranding: {
      type: Object as PropType<AdTemplateMarket>,
      required: true
    },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const brandContext = injectBrand();

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const classesRef = useMultiFtrCardStyles(brandContext?.adTemplateBranding);

    const marketIds = composeMultiFtrMarkets(props.eventId);
    const homeAwayOptions = composeHomeAwayOptions(marketIds);
    const event = composeEvent(props.eventId) as ComputedRef<SportEvent>;
    const fixtureStartDate = composeDate(event.value.id);

    return {
      marketIds,
      homeAwayOptions,
      event,
      fixtureStartDate,
      tags: computed(() => {
        return [
          event.value?.competitors.away.name.micro,
          event.value?.competitors.home.name.micro
        ];
      }),
      marketNameStyle: {
        get(brand: Brand | undefined) {
          return brand && brand.adTemplate.market.marketNameFont;
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.marketNameFont = value;
        }
      },
      fixtureTimeFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureTimeFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureTimeFont = value;
        }
      },
      fixtureSportFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureSportFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureSportFont = value;
        }
      },
      classes: classesRef.value
    };
  }
});

const useMultiFtrCardStyles = createUseStyles({
  multiFtrContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "column"
  },
  marketCard: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    height: "100%",
    border: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    background: props => props.cardBackgroundColor,
    borderRadius: props => `${props.borderRadius}px`,
    paddingBottom: "12px"
  },
  markets: {
    width: "100%",
    display: "flex",
    flex: 1,
    flexDirection: "column",
    margin: "-4px 0"
  },
  marketRow: {
    width: "100%",
    display: "flex",
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between"
  },
  marketTitle: {
    width: "80px",
    fontSize: "12px",
    fontWeight: "normal",
    textAlign: "center",
    color: "#6c6d6f",
    alignSelf: "center"
  },
  marketButton: {
    flex: 1,
    minHeight: "30px",
    margin: "4px 8px",
    maxWidth: "72px"
  },
  eventName: {
    marginBottom: "16px",
    borderBottom: props => `1px solid ${props.marketBorderColor || "#dcddf"}`
  }
});
</script>
